import { BEATS_PER_MEASURE, MIDDLE_C, TICKS_PER_LOOP } from '@arpeggi/shared'
import { el } from '@elemaudio/core'

export function freqMap(midi: number) {
  return Math.pow(2, midi / 12.0)
}

export function clip(i: number, low: number, high: number) {
  return Math.min(Math.max(i, low), high)
}

export function fmod(a: number, b: number) {
  return Number((a - Math.floor(a / b) * b).toPrecision(8))
}

export function mapRange(i: number, iMin: number, iMax: number, oMin: number, oMax: number) {
  return ((i - iMin) * (oMax - oMin)) / (iMax - iMin) + oMin
}

export function calculatePlaybackRate(semitone: number) {
  return Math.pow(2, (semitone - MIDDLE_C) / 12)
}
export function wrap(i: number, min: number, max: number) {
  let retVal
  let range = max - min

  if (i < max && i >= min) {
    return i
  }

  if (max === min) {
    return min
  }

  if (i < min) {
    retVal = i
    while (retVal < min) retVal += range
  } else retVal = fmod(i - min, range) + min

  return retVal
}

export function rotate(arr: any[], count: number) {
  count -= arr.length * Math.floor(count / arr.length)
  arr.push.apply(arr, arr.splice(0, count))
  return arr
}

export function randomBooleanArray(length: number) {
  const arr: any = []
  for (var i = 0; i < length; i++) {
    arr[i] = Math.random() > 0.75
  }
  return arr
}

export function emptyBooleanArray(length: number) {
  const arr: any = []
  for (var i = 0; i < length; i++) {
    arr[i] = 0
  }
  return arr
}

export function convertBooleanArray(booleanArray: any) {
  let arr = Array(booleanArray.length)
  for (var i = 0; i < booleanArray.length; i++) {
    if (booleanArray[i] === true) arr[i] = 1
    else arr[i] = 0
  }
  return arr
}

export function deepCopy(arr: any) {
  return JSON.parse(JSON.stringify(arr))
}

export function shiftColumnDown(grid: string | any[], col: string | number) {
  let temp = deepCopy(grid) // deep copy
  for (var i = 0; i < grid.length; i++) {
    // in each row
    let below = (i + 1) % grid.length
    grid[below][col] = temp[i][col]
  }
}

export function shiftColumnUp(grid: string | any[], col: string | number) {
  let temp = deepCopy(grid) // deep copy
  for (var i = 0; i < grid.length; i++) {
    // in each row
    let invert = grid.length - i - 1
    let above = invert - 1

    if (above < 0) {
      // Wrap
      above = grid.length - Math.abs(0 - above)
    }
    grid[above][col] = temp[invert][col]
  }
}

export function mirror(arr: string | any[]) {
  let halfway = Math.floor(arr.length / 2)
  let temp = new Array(arr.length).fill(false)

  for (let i = 0; i < arr.length; i++) {
    if (i < halfway) {
      temp[i] = arr[i]
    } else {
      temp[i] = arr[arr.length - i - 1]
    }
  }
  return temp
}

export function ftom(x: any) {
  return Math.log2(x / 440.0) + 69.0
}

export function mtof(x: any) {
  return Math.pow(2, x - 69.0) * 440
}

export function tempoToMs(tempo: number, subDiv = 16) {
  const beatLen = 1 / (tempo / 60)
  return ((beatLen * 4) / subDiv) * 1000
}

export function upsample(a, factor) {
  return Array.from({ length: a.length * factor }).map(function (_, i) {
    return a[Math.floor(i / factor)]
  })
}

export function bpmToHz(tempo: number, subDiv = 16) {
  return (tempo / (60 * 4)) * subDiv
}

export function ticksToHz(ticks: number, tempo: number, inverse: boolean = false) {
  const denominator = (ticks / TICKS_PER_LOOP) * 60 * BEATS_PER_MEASURE * (4 / tempo)
  if (inverse) return denominator
  return 1 / denominator
}

export function select2(g, a, b) {
  return el.add(el.mul(g, b), el.mul(el.sub(1, g), a))
}

export function selectN(x, ...args) {
  if (args.length < 2) {
    console.log('Not enough args for selector')
    return 0
  }

  if (args.length === 2) {
    return select2(el.ge(x, 0), args[0], args[1])
  }

  return select2(el.ge(x, 0), args[0], selectN(el.sub(x, 1), ...args.slice(1)))
}

export function accum(x) {
  return el.pole(1, x)
}

export function repeat2(x) {
  return [...x, ...x]
}

export function repeat4(x) {
  return [...x, ...x, ...x, ...x]
}

export function zerosWithPulse(n) {
  const zerosWithPulse = Array(n - 1).fill(0)
  zerosWithPulse.push(1)
  return zerosWithPulse.reverse()
}

export function or(a, b) {
  return el.ge(el.add(a, b), 0)
}

export function zeros(n) {
  return Array.from({ length: n }).fill(0)
}

export function not(x) {
  return el.sub(1, x)
}

export function treeAdd(array: any[]) {
  if (array.length > 8) {
    return el.add(treeAdd(array.slice(7)), ...array.slice(0, 7))
  }
  return el.add(...array)
}

export function tempoToIntervalMs(bpm: number, clockDivision: number) {
  let msPerBeat = 60000 / bpm
  let msPerTick = msPerBeat / clockDivision
  return msPerTick / 2
}

export const ticksToSeconds = (ticks: number, tempo: number): number => {
  return (60 / tempo) * (ticks / TICKS_PER_LOOP) * 16
}
